import { type Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type { ApiResponse, PaginatedListResponse } from '@shared/http/types';

import { createHttpParams, mapBaseListQueryToDto } from '@shared/http/utils';

import type { FirmDto } from './dto';
import type { Firm, FirmsQuery, FirmPayload } from './models';
import { mapFirmFromDto, mapFirmPayloadToDto } from './mappers';

import { mapDataNonNull, mapPaginatedList } from '@shared/http/operators';
import { buildApiEndpoint, buildHttpContext, type RequestOptions } from '@core/http';

@Injectable({
  providedIn: 'root',
})
export class FirmRepository {
  private readonly http = inject(HttpClient);

  getFirms(query?: FirmsQuery): Observable<PaginatedListResponse<Firm>> {
    return this.http
      .get<PaginatedListResponse<FirmDto>>(buildApiEndpoint('firms.list'), {
        params: query ? createHttpParams(mapBaseListQueryToDto(query)) : undefined,
      })
      .pipe(mapPaginatedList(mapFirmFromDto));
  }

  getFirm(id: number): Observable<Firm> {
    return this.http
      .get<ApiResponse<FirmDto>>(buildApiEndpoint('firms.detail', { firmId: id }))
      .pipe(mapDataNonNull(mapFirmFromDto));
  }

  createFirm(payload: FirmPayload, options?: RequestOptions): Observable<Firm> {
    return this.http
      .post<ApiResponse<FirmDto>>(buildApiEndpoint('firms.list'), mapFirmPayloadToDto(payload), {
        context: buildHttpContext(options),
      })
      .pipe(mapDataNonNull(mapFirmFromDto));
  }

  updateFirm(id: number, payload: FirmPayload, options?: RequestOptions): Observable<Firm> {
    return this.http
      .put<ApiResponse<FirmDto>>(buildApiEndpoint('firms.detail', { firmId: id }), mapFirmPayloadToDto(payload), {
        context: buildHttpContext(options),
      })
      .pipe(mapDataNonNull(mapFirmFromDto));
  }

  deleteFirm(id: number): Observable<void> {
    return this.http.delete<void>(buildApiEndpoint('firms.detail', { firmId: id }));
  }
}
